import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  CodeIcon,
  LightbulbIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  DatabaseIcon,
  CloudIcon,
  ToolsIcon,
  DesktopIcon,
  MobileAltIcon,
} from "./IconComponents";
import SEO from "./SEO";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

// const HeroTitle = styled.h1`
//   font-size: 3.5rem;
//   font-weight: 700;
//   margin-bottom: 1.5rem;
//   line-height: 1.2;

//   @media (max-width: 768px) {
//     font-size: 2.5rem;
//   }
// `;

// const HeroSubtitle = styled.p`
//   font-size: 1.3rem;
//   opacity: 0.9;
//   max-width: 700px;
//   margin: 0 auto 2rem;
//   line-height: 1.6;
// `;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const PricingSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const PricingCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }

  &.popular {
    border-color: var(--primary-color);
    background: var(--card-background);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const PricingTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const PricingDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const PricingFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const IndividuelleSoftwareentwicklung: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <LightbulbIcon />,
      title: "Maßgeschneiderte Lösungen",
      description:
        "Jede Software wird speziell für Ihre Geschäftsanforderungen entwickelt, um maximale Effizienz und Benutzerfreundlichkeit zu gewährleisten.",
    },
    {
      icon: <RocketIcon />,
      title: "Skalierbare Architektur",
      description:
        "Unsere Lösungen wachsen mit Ihrem Unternehmen mit und können flexibel an veränderte Anforderungen angepasst werden.",
    },
    {
      icon: <LockIcon />,
      title: "Höchste Sicherheitsstandards",
      description:
        "Wir implementieren modernste Sicherheitstechnologien und befolgen branchenübliche Best Practices zum Schutz Ihrer Daten.",
    },
    {
      icon: <UsersIcon />,
      title: "Benutzerorientiertes Design",
      description:
        "Intuitive Benutzeroberflächen und optimierte Workflows sorgen für hohe Akzeptanz und Produktivität bei Ihren Mitarbeitern.",
    },
  ];

  const process = [
    {
      title: "Analyse & Beratung",
      description:
        "Detaillierte Analyse Ihrer Geschäftsprozesse und Anforderungen, um die optimale Lösung zu definieren.",
    },
    {
      title: "Konzeption & Design",
      description:
        "Erstellung eines umfassenden Konzepts mit Wireframes, Prototypen und technischer Architektur.",
    },
    {
      title: "Entwicklung & Testing",
      description:
        "Agile Entwicklung mit regelmäßigen Reviews, umfassenden Tests und Qualitätssicherung.",
    },
    {
      title: "Deployment & Support",
      description:
        "Professionelle Einführung, Schulungen und kontinuierlicher Support für optimale Nutzung.",
    },
  ];

  const benefits = [
    {
      title: "Erhöhte Effizienz",
      description:
        "Automatisierung wiederkehrender Aufgaben und Optimierung von Arbeitsabläufen steigern die Produktivität um durchschnittlich 40%.",
    },
    {
      title: "Wettbewerbsvorteile",
      description:
        "Einzigartige Software-Features, die speziell auf Ihre Geschäftsmodelle zugeschnitten sind, verschaffen Ihnen entscheidende Marktvorteile.",
    },
    {
      title: "Kostenreduktion",
      description:
        "Langfristige Kosteneinsparungen durch Eliminierung manueller Prozesse und Reduzierung von Fehlern.",
    },
    {
      title: "Bessere Datenqualität",
      description:
        "Integrierte Validierung und automatische Datenverarbeitung sorgen für konsistente, zuverlässige Informationen.",
    },
    {
      title: "Flexibilität & Anpassbarkeit",
      description:
        "Schnelle Anpassung an Marktveränderungen und neue Geschäftsanforderungen ohne Abhängigkeit von Standard-Software-Herstellern.",
    },
    {
      title: "Vollständige Kontrolle",
      description:
        "Sie behalten die vollständige Kontrolle über Ihre Software, Daten und zukünftige Entwicklungen.",
    },
  ];

  const technologies = [
    {
      icon: <CodeIcon />,
      name: "Frontend",
      description: "React, Angular, Vue.js, TypeScript",
    },
    {
      icon: <DatabaseIcon />,
      name: "Backend",
      description: ".NET, Node.js, Python, Java",
    },
    {
      icon: <CloudIcon />,
      name: "Cloud",
      description: "Azure, AWS, Docker, Kubernetes",
    },
    {
      icon: <MobileAltIcon />,
      name: "Mobile",
      description: "React Native, Flutter, Xamarin",
    },
    {
      icon: <ToolsIcon />,
      name: "DevOps",
      description: "CI/CD, Git, Jenkins, Azure DevOps",
    },
    {
      icon: <DesktopIcon />,
      name: "Desktop",
      description: "WPF, Electron, .NET MAUI",
    },
  ];

  const caseStudies = [
    {
      title: "E-Commerce Plattform für Mittelstand",
      description:
        "Entwicklung einer maßgeschneiderten E-Commerce-Lösung mit integriertem Warenwirtschaftssystem für einen mittelständischen Händler.",
      result: {
        title: "Ergebnisse:",
        description:
          "300% Steigerung der Online-Verkäufe, 60% Reduktion der Bearbeitungszeiten, nahtlose Integration bestehender Systeme",
      },
    },
    {
      title: "Digitalisierung von Produktionsprozessen",
      description:
        "Automatisierung und Digitalisierung der Fertigungsprozesse eines Industrieunternehmens mit IoT-Integration und Echtzeit-Monitoring.",
      result: {
        title: "Ergebnisse:",
        description:
          "25% Effizienzsteigerung, 40% weniger Produktionsausfälle, vollständige Transparenz der Produktionsdaten",
      },
    },
    {
      title: "CRM-System für Dienstleister",
      description:
        "Entwicklung eines branchenspezifischen CRM-Systems mit automatisierten Workflows und integrierter Rechnungsstellung.",
      result: {
        title: "Ergebnisse:",
        description:
          "50% schnellere Kundenbearbeitung, 35% mehr Kundenzufriedenheit, vollständige Automatisierung der Rechnungserstellung",
      },
    },
  ];

  const pricingModels = [
    {
      title: "Festpreis-Projekt",
      description:
        "Komplette Projektabwicklung zum vereinbarten Festpreis mit definierten Meilensteinen und Lieferzeiten.",
      features: [
        "Detaillierte Projektplanung",
        "Feste Kosten und Termine",
        "Regelmäßige Updates",
        "Vollständige Dokumentation",
        "3 Monate kostenloser Support",
      ],
    },
    {
      title: "Time & Material",
      description:
        "Flexible Abrechnung nach tatsächlichem Aufwand - ideal für iterative Entwicklung und sich ändernde Anforderungen.",
      features: [
        "Maximale Flexibilität",
        "Transparente Zeiterfassung",
        "Agile Entwicklungsansätze",
        "Kontinuierliche Anpassungen",
        "Monatliche Abrechnung",
      ],
      popular: true,
    },
    {
      title: "Dedicated Team",
      description:
        "Ein dediziertes Entwicklungsteam arbeitet ausschließlich an Ihrem Projekt - langfristige Partnerschaft für komplexe Vorhaben.",
      features: [
        "Exklusives Entwicklungsteam",
        "Langfristige Zusammenarbeit",
        "Tiefes Verständnis Ihrer Domäne",
        "Kontinuierliche Weiterentwicklung",
        "Direkte Kommunikation",
      ],
    },
  ];

  const faqs = [
    {
      question:
        "Wie lange dauert die Entwicklung einer individuellen Software?",
      answer:
        "Die Entwicklungszeit hängt vom Umfang und der Komplexität des Projekts ab. Einfache Anwendungen können in 2-3 Monaten realisiert werden, während komplexe Enterprise-Lösungen 6-12 Monate oder länger benötigen können. In der Analysephase erstellen wir einen detaillierten Zeitplan.",
    },
    {
      question: "Welche Technologien verwenden Sie für die Entwicklung?",
      answer:
        "Wir setzen auf bewährte und zukunftssichere Technologien wie Java, React, Angular, Node.js, Python und moderne Cloud-Plattformen. Die Technologie-Auswahl erfolgt basierend auf Ihren spezifischen Anforderungen und bestehender IT-Infrastruktur.",
    },
    {
      question: "Bieten Sie auch Wartung und Support nach der Entwicklung?",
      answer:
        "Ja, wir bieten umfassende Wartungs- und Support-Services. Dies umfasst Bug-Fixes, Updates, Sicherheitspatches und Weiterentwicklungen. Wir stehen Ihnen auch langfristig als verlässlicher Partner zur Verfügung.",
    },
    {
      question: "Können Sie bestehende Systeme integrieren?",
      answer:
        "Selbstverständlich. Die Integration bestehender Systeme (ERP, CRM, Datenbanken) ist einer unserer Schwerpunkte. Wir sorgen für nahtlose Datenübertragung und einheitliche Benutzeroberflächen ohne Unterbrechung Ihrer Geschäftsprozesse.",
    },
    {
      question: "Wie stellen Sie die Qualität der Software sicher?",
      answer:
        "Wir verwenden moderne Entwicklungsmethoden wie Test-Driven Development, automatisierte Tests, Code-Reviews und kontinuierliche Integration. Zusätzlich führen wir ausführliche Qualitätssicherungstests und Benutzerakzeptanztests durch.",
    },
  ];

  const SectionSubtitle = styled.p`
    font-size: 1.2rem;
    text-align: center;
    max-width: 800px;
    margin: 0 auto 3rem;
    color: var(--text-color);
  `;

  return (
    <PageContainer>
      <SEO
        title="Individuelle Softwareentwicklung für Unternehmen"
        description="Maßgeschneiderte Softwarelösungen von Byte Forge: skalierbar, sicher und exakt auf Ihre Geschäftsprozesse zugeschnitten. Jetzt unverbindlich beraten lassen."
        path="/individuelle-softwareentwicklung"
        keywords="individuelle Softwareentwicklung, maßgeschneiderte Software, Softwareentwicklung Unternehmen, Custom Software"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Individuelle <span>Softwareentwicklung</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Maßgeschneiderte Softwarelösungen, die perfekt auf Ihre
          Geschäftsanforderungen zugeschnitten sind. Wir entwickeln innovative,
          skalierbare und sichere Anwendungen, die Ihrem Unternehmen einen
          echten Wettbewerbsvorteil verschaffen.
        </SectionSubtitle>
      </Container>
      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>individuelle Software</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>Entwicklungsprozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Technologien</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>Projekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      {/*<PricingSection>*/}
      {/*  <Container>*/}
      {/*    <SectionTitle>Unsere <span>Zusammenarbeitsmodelle</span></SectionTitle>*/}
      {/*    <PricingGrid>*/}
      {/*      {pricingModels.map((pricing, index) => (*/}
      {/*        <PricingCard key={index} className={pricing.popular ? 'popular' : ''}>*/}
      {/*          {pricing.popular && <PopularBadge>Beliebt</PopularBadge>}*/}
      {/*          <PricingTitle>{pricing.title}</PricingTitle>*/}
      {/*          <PricingDescription>{pricing.description}</PricingDescription>*/}
      {/*          <PricingFeatures>*/}
      {/*            {pricing.features.map((feature, featureIndex) => (*/}
      {/*              <li key={featureIndex}>{feature}</li>*/}
      {/*            ))}*/}
      {/*          </PricingFeatures>*/}
      {/*        </PricingCard>*/}
      {/*      ))}*/}
      {/*    </PricingGrid>*/}
      {/*  </Container>*/}
      {/*</PricingSection>*/}

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für Ihre <span>individuelle Softwarelösung?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam die perfekte Lösung für Ihre
            Geschäftsanforderungen entwickeln. Kontaktieren Sie uns für eine
            kostenlose Beratung.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>

    </PageContainer>
  );
};

export default IndividuelleSoftwareentwicklung;
